import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setloggedIn } from "../../../store/Slices/userSlice.js";
import { removeCookies } from "../../../services/Cookies.js";
import { FaBars, FaTimes, FaRocket, FaChevronDown } from "react-icons/fa";

const Header1 = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    const isActive = (path) => location.pathname === path;

    // Logout Function
    const logoutHandler = () => {
        localStorage.removeItem("token");
        dispatch(setloggedIn(false));
        removeCookies("token");
        navigate("/");
    };

    // Scroll Effect
    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Close mobile menu when route changes
    useEffect(() => {
        setIsMobileMenuOpen(false);
        setIsDropdownOpen(false);
    }, [location.pathname]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Main navigation items (always visible)
    const mainNavItems = [
        { path: "/", label: "Home" },
        { path: "/about", label: "About" },
        { path: "/plans", label: "Plans" },
        { path: "/contact", label: "Contact" }
    ];

    // Dropdown items (grouped)
    const dropdownItems = [
        { path: "/features", label: "Features" },
        { path: "/solutions", label: "Solutions" },
        { path: "/services", label: "Services" },
        { path: "/gallery", label: "Gallery" }
    ];

    // All items for mobile menu
    const allNavItems = [...mainNavItems, ...dropdownItems];

    return (
        <div className="relative">
            <header
                className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
                    isScrolled 
                        ? "bg-white/95 shadow-xl backdrop-blur-xl border-b border-gray-200" 
                        : "bg-transparent"
                }`}
            >
                <div className="header-upper p-2 md:p-3 lg:p-4 md:px-6 lg:px-8">
                    <div className="header-inner flex justify-between items-center max-w-7xl mx-auto">
                        {/* Logo */}
                        <div className="logo flex-shrink-0">
                            <Link to="/" className="flex items-center gap-2 md:gap-3 group">
                                <div className="relative">
                                    <img
                                        src="/assets/logo/logo.png"
                                        alt="Educational Eternity Logo"
                                        className="h-10 md:h-12 lg:h-16 object-contain rounded-full transition-all duration-300 group-hover:scale-110 group-hover:rotate-12 shadow-lg"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full opacity-0 group-hover:opacity-20 blur-xl transition-opacity"></div>
                                </div>
                                <span className={`hidden sm:block text-base md:text-lg lg:text-xl font-bold transition-colors duration-300 whitespace-nowrap ${
                                    isScrolled ? "text-gray-900" : "text-white"
                                }`}>
                                    <span className="hidden lg:inline">Educational Eternity</span>
                                    <span className="lg:hidden">Edu Eternity</span>
                                </span>
                            </Link>
                        </div>

                        {/* Desktop Navigation */}
                        <nav className="hidden lg:flex items-center space-x-1 xl:space-x-2">
                            {/* Main Navigation Items */}
                            <ul className="flex items-center space-x-1 xl:space-x-2">
                                {mainNavItems.map((item) => (
                                    <li key={item.path}>
                                        <Link
                                            to={item.path}
                                            className={`relative px-3 xl:px-4 py-2 text-sm xl:text-base font-semibold transition-all duration-300 rounded-lg whitespace-nowrap ${
                                                isActive(item.path)
                                                    ? isScrolled
                                                        ? "text-blue-600 bg-blue-50"
                                                        : "text-white bg-white/20"
                                                    : isScrolled
                                                    ? "text-gray-700 hover:text-blue-600 hover:bg-gray-50"
                                                    : "text-white hover:text-blue-200 hover:bg-white/10"
                                            }`}
                                        >
                                            {item.label}
                                            {isActive(item.path) && (
                                                <span className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1/2 h-0.5 rounded-full ${
                                                    isScrolled ? "bg-blue-600" : "bg-white"
                                                }`}></span>
                                            )}
                                        </Link>
                                    </li>
                                ))}
                            </ul>

                            {/* Dropdown Menu */}
                            <div className="relative" ref={dropdownRef}>
                                <button
                                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                    className={`relative px-3 xl:px-4 py-2 text-sm xl:text-base font-semibold transition-all duration-300 rounded-lg flex items-center gap-1 whitespace-nowrap ${
                                        dropdownItems.some(item => isActive(item.path))
                                            ? isScrolled
                                                ? "text-blue-600 bg-blue-50"
                                                : "text-white bg-white/20"
                                            : isScrolled
                                            ? "text-gray-700 hover:text-blue-600 hover:bg-gray-50"
                                            : "text-white hover:text-blue-200 hover:bg-white/10"
                                    }`}
                                >
                                    More
                                    <FaChevronDown className={`text-xs transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                                </button>

                                {/* Dropdown Content */}
                                {isDropdownOpen && (
                                    <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
                                        {dropdownItems.map((item) => (
                                            <Link
                                                key={item.path}
                                                to={item.path}
                                                className={`block px-4 py-3 text-sm font-medium transition-colors duration-200 ${
                                                    isActive(item.path)
                                                        ? "text-blue-600 bg-blue-50"
                                                        : "text-gray-700 hover:text-blue-600 hover:bg-gray-50"
                                                }`}
                                            >
                                                {item.label}
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Desktop CTA Button */}
                            <button 
                                className={`ml-2 group relative font-bold px-4 xl:px-6 py-2 xl:py-3 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center gap-2 overflow-hidden whitespace-nowrap text-sm xl:text-base ${
                                    isScrolled
                                        ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white"
                                        : "bg-white text-blue-600 hover:bg-blue-50"
                                }`}
                                onClick={() => window.open('https://featuretour.educationaleternity.com', '_blank')}
                            >
                                <span className="relative z-10 flex items-center gap-2">
                                    <FaRocket className="group-hover:rotate-12 transition-transform" /> 
                                    <span className="hidden xl:inline">Take a Tour</span>
                                    <span className="xl:hidden">Tour</span>
                                </span>
                                {isScrolled && (
                                    <div className="absolute inset-0 bg-gradient-to-r from-blue-700 to-indigo-700 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                )}
                            </button>
                        </nav>

                        {/* Tablet Navigation (Medium screens) */}
                        <nav className="hidden md:flex lg:hidden items-center space-x-2">
                            <ul className="flex items-center space-x-1">
                                {mainNavItems.slice(0, 3).map((item) => (
                                    <li key={item.path}>
                                        <Link
                                            to={item.path}
                                            className={`relative px-2 py-2 text-sm font-semibold transition-all duration-300 rounded-lg ${
                                                isActive(item.path)
                                                    ? isScrolled
                                                        ? "text-blue-600 bg-blue-50"
                                                        : "text-white bg-white/20"
                                                    : isScrolled
                                                    ? "text-gray-700 hover:text-blue-600 hover:bg-gray-50"
                                                    : "text-white hover:text-blue-200 hover:bg-white/10"
                                            }`}
                                        >
                                            {item.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                            <button
                                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                                className={`px-3 py-2 text-sm font-semibold rounded-lg transition-all ${
                                    isScrolled ? "text-gray-700 hover:bg-gray-50" : "text-white hover:bg-white/10"
                                }`}
                            >
                                More
                            </button>
                        </nav>

                        {/* Mobile Menu Button */}
                        <button
                            className={`lg:hidden text-2xl z-50 relative p-2 rounded-lg transition-all duration-300 ${
                                isScrolled ? "text-gray-900 hover:bg-gray-100" : "text-white hover:bg-white/10"
                            }`}
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            aria-label="Toggle menu"
                        >
                            {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
                        </button>
                    </div>
                </div>

                {/* Mobile Menu Overlay */}
                {isMobileMenuOpen && (
                    <div 
                        className="lg:hidden fixed inset-0 bg-black/50 z-40 mt-16"
                        onClick={() => setIsMobileMenuOpen(false)}
                    ></div>
                )}

                {/* Mobile Navigation Menu */}
                <nav
                    className={`lg:hidden fixed top-16 left-0 w-full bg-white shadow-2xl z-40 transition-transform duration-300 overflow-y-auto ${
                        isMobileMenuOpen 
                            ? "transform translate-x-0 max-h-[calc(100vh-4rem)]" 
                            : "transform -translate-x-full"
                    }`}
                >
                    <div className="px-4 py-6">
                        <ul className="space-y-2">
                            {allNavItems.map((item) => (
                                <li key={item.path}>
                                    <Link
                                        to={item.path}
                                        className={`block px-4 py-3 text-base font-semibold rounded-lg transition-all duration-300 ${
                                            isActive(item.path)
                                                ? "text-blue-600 bg-blue-50"
                                                : "text-gray-700 hover:text-blue-600 hover:bg-gray-50"
                                        }`}
                                        onClick={() => setIsMobileMenuOpen(false)}
                                    >
                                        {item.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                        
                        {/* Mobile CTA Button */}
                        <div className="mt-6 pt-6 border-t border-gray-200">
                            <button 
                                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold px-6 py-4 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
                                onClick={() => {
                                    window.open('https://featuretour.educationaleternity.com', '_blank');
                                    setIsMobileMenuOpen(false);
                                }}
                            >
                                <FaRocket /> Take a Tour
                            </button>
                        </div>
                    </div>
                </nav>
            </header>
        </div>
    );
};

export default Header1;
